import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { t } from 'i18next';

import { CellRendererForAgGrid, LeafRowCellRenderer } from 'core/elements';
import { AgGridFilterOption, SortDirection } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { formatDate, formatQuantity, isPromotedSingleChild } from 'core/utils';
import { ShipDaysCell } from 'parts/elements';
import { PricingHelper } from 'pricing/helpers';

import { InventoryQuantityCell, LocationCell } from './cells';
import { getLocationValue } from './utils';

interface IProps {
  shipTypeCheckEnabled: boolean;
  shipTypeError?: string;
  isAlternateVersionEnabled: boolean;
  lotDetailsAvailable: boolean;
  costDecimals?: number;
}

type CellComponent = (params: ICellRendererParams) => JSX.Element | string | number | null;

interface IVendorInventoryProps {
  isAlternateVersionEnabled?: boolean;
  alternateInventoryEnabled?: boolean;
}

const renderOnGroupOnly = (Cell?: CellComponent) =>
  function GroupOnlyCellRenderer(params: ICellRendererParams) {
    const isGroupRow = !!params.node?.group;
    const isPromoted = isPromotedSingleChild(params);

    if (!isGroupRow && !isPromoted) {
      return null;
    }

    if (Cell) {
      const data = isGroupRow ? params.node.allLeafChildren?.[0]?.data : params.node?.data;

      return Cell({ ...params, data });
    }

    return <CellRendererForAgGrid {...params} />;
  };

const getLotDetailsColumns = () => ({
  lotNumberColumn: {
    colId: 'lotNumber',
    headerName: t('headerReelId'),
    maxWidth: 300,
    field: 'lotNumber',
    filter: 'agTextColumnFilter',
    sortable: false,
    enableRowGroup: false,
    cellRenderer: LeafRowCellRenderer,
  },
  reelLengthColumn: {
    colId: 'reelLength',
    headerName: t('headerReelLength'),
    maxWidth: 130,
    field: 'reelLength',
    filterParams: {
      defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
    },
    filter: 'agNumberColumnFilter',
    sortable: false,
    enableRowGroup: false,
    cellRenderer: LeafRowCellRenderer,
  },
  lotManufacturerNameColumn: {
    colId: 'lotManufacturerName',
    headerName: t('headerManufacturer'),
    maxWidth: 300,
    field: 'lotManufacturerName',
    filter: 'agTextColumnFilter',
    sortable: false,
    enableRowGroup: false,
    cellRenderer: LeafRowCellRenderer,
  },
});

const getInventoryColumns = (
  shipTypeCheckEnabled: boolean,
  shipTypeError: string,
  isAlternateVersionEnabled: boolean,
  costDecimals: number
) => ({
  shipTimeColumn: {
    colId: 'shipDays',
    headerName: t('headerShipDays'),
    field: 'shipDays',
    minWidth: 100,
    width: 100,
    maxWidth: 150,
    sort: SortDirection.ASC,
    enableRowGroup: false,
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: AgGridFilterOption.LESS_THAN_OR_EQUAL,
    },
    aggFunc: 'first',
    cellRenderer: renderOnGroupOnly(ShipDaysCell),
    cellRendererParams: {
      loaderEnabled: shipTypeCheckEnabled,
      error: shipTypeError,
    },
    valueGetter: ({ data }) => data && data?.shipDays,
  },
  uomColumn: {
    colId: 'uom',
    headerName: t('headerUom'),
    field: 'uom',
    minWidth: 100,
    width: 100,
    maxWidth: 150,
    filter: 'agTextColumnFilter',
    aggFunc: 'first',
    cellRenderer: renderOnGroupOnly(),
    valueFormatter: ({ value }) => value?.toUpperCase(),
  },
  quantityAvailableColumn: {
    colId: 'quantityAvailable',
    headerName: t('headerAvailable'),
    field: 'quantityAvailable',
    maxWidth: 150,
    enableRowGroup: false,
    aggFunc: 'first',
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
    },
    valueGetter: ({ data }) => data && (Number(data.quantityAvailable) || 0),
    cellStyle: { fontWeight: 'bold' },
    cellRenderer: renderOnGroupOnly(),
    valueFormatter: ({ value }) => NumberHelper.format({ amount: Number(value) }),
  },
  quantityRestrictedAvailableColumn: {
    colId: 'quantityRestrictedAvailable',
    headerName: t('headerRestrictedAvailable'),
    field: 'quantityRestrictedAvailable',
    maxWidth: 170,
    enableRowGroup: false,
    aggFunc: 'first',
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
    },
    valueGetter: ({ data }) => data && (Number(data.quantityRestrictedAvailable) || 0),
    cellStyle: { fontWeight: 'bold' },
    cellRenderer: renderOnGroupOnly(),
    valueFormatter: ({ value }) => NumberHelper.format({ amount: Number(value) }),
  },
  quantityAllocatedColumn: {
    colId: 'quantityAllocated',
    headerName: t('headerAllocated'),
    field: 'quantityAllocated',
    maxWidth: 150,
    enableRowGroup: false,
    aggFunc: 'first',
    valueGetter: ({ data }) => data && (Number(data.quantityAllocated) || 0),
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
    },
    cellRenderer: renderOnGroupOnly(InventoryQuantityCell),
    cellRendererParams: {
      config: {
        dataField: 'soAllocated',
        tableId: 'salesOrdersAllocatedTable',
        title: t('titleSalesOrdersAllocated'),
      },
      isAlternateVersionEnabled,
    },
    valueFormatter: ({ value }) => NumberHelper.format({ amount: Number(value) }),
  },
  quantityOnHandColumn: {
    colId: 'quantityOnHand',
    headerName: t('headerOnHand'),
    field: 'quantityOnhand',
    maxWidth: 130,
    enableRowGroup: false,
    aggFunc: 'first',
    valueGetter: ({ data }) => data && (Number(data.quantityOnhand) || 0),
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
    },
    cellRenderer: renderOnGroupOnly(),
    valueFormatter: ({ value }) => NumberHelper.format({ amount: Number(value) }),
  },
  quantityBackOrderedColumn: {
    colId: 'quantityBackOrdered',
    headerName: t('headerBackOrdered'),
    field: 'quantityBackorder',
    maxWidth: 150,
    enableRowGroup: false,
    aggFunc: 'first',
    valueGetter: ({ data }) => data && (Number(data.quantityBackorder) || 0),
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
    },
    cellRenderer: renderOnGroupOnly(InventoryQuantityCell),
    cellRendererParams: {
      config: {
        dataField: 'soBackorder',
        tableId: 'salesOrdersBackOrderedTable',
        title: t('titleSalesOrdersBackOrdered'),
      },
      isAlternateVersionEnabled,
    },
    valueFormatter: ({ value }) => NumberHelper.format({ amount: Number(value) }),
  },
  quantityOnOrderColumn: {
    colId: 'quantityOnOrder',
    headerName: t('headerOnOrder'),
    field: 'quantityOnorder',
    maxWidth: 130,
    enableRowGroup: false,
    aggFunc: 'first',
    valueGetter: ({ data }) => data && (Number(data.quantityOnorder) || 0),
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
    },
    cellRenderer: renderOnGroupOnly(InventoryQuantityCell),
    cellRendererParams: {
      config: {
        dataField: 'poData',
      },
      isAlternateVersionEnabled,
    },
    valueFormatter: ({ value }) => NumberHelper.format({ amount: Number(value) }),
  },
  updatedAtColumn: {
    colId: 'updatedAt',
    headerName: t('headerLastUpdated'),
    enableRowGroup: false,
    field: 'lastUpdateDate',
    filter: 'agDateColumnFilter',
    filterParams: {
      defaultOption: AgGridFilterOption.GREATER_THAN,
    },
    valueFormatter: ({ value }) => value && formatDate(value),
  },
  costColumn: {
    colId: 'cost',
    headerName: t('headerStandardCost'),
    enableRowGroup: false,
    field: 'standardCost',
    aggFunc: 'first',
    valueGetter: ({ data }) => data && (Number(data.standardCost) || 0),
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
    },
    cellRenderer: renderOnGroupOnly(),
    valueFormatter: ({ value }) =>
      value != null
        ? PricingHelper.formatPricing({
            amount: value,
            decimals: costDecimals,
            fixedDecimals: true,
          })
        : '',
  },
});

export const columnDef = ({
  shipTypeCheckEnabled,
  shipTypeError,
  isAlternateVersionEnabled,
  lotDetailsAvailable,
  costDecimals = PricingHelper.DEFAULT_COST_DECIMALS,
}: IProps): ColDef[] => {
  const { lotNumberColumn, reelLengthColumn, lotManufacturerNameColumn } = getLotDetailsColumns();

  const {
    shipTimeColumn,
    uomColumn,
    quantityAvailableColumn,
    quantityRestrictedAvailableColumn,
    quantityAllocatedColumn,
    quantityOnHandColumn,
    quantityBackOrderedColumn,
    quantityOnOrderColumn,
    updatedAtColumn,
    costColumn,
  } = getInventoryColumns(
    shipTypeCheckEnabled,
    shipTypeError,
    isAlternateVersionEnabled,
    costDecimals
  );

  // Restricted (segmented) stock exists only on the Unified inventory feed.
  const restrictedAvailableColumns = isAlternateVersionEnabled
    ? [quantityRestrictedAvailableColumn]
    : [];

  if (!lotDetailsAvailable) {
    return [
      shipTimeColumn,
      uomColumn,
      quantityAvailableColumn,
      ...restrictedAvailableColumns,
      quantityAllocatedColumn,
      quantityOnHandColumn,
      quantityBackOrderedColumn,
      quantityOnOrderColumn,
      ...(isAlternateVersionEnabled ? [] : [updatedAtColumn, costColumn]),
    ];
  }

  return [
    shipTimeColumn,
    uomColumn,
    lotNumberColumn,
    reelLengthColumn,
    lotManufacturerNameColumn,
    quantityAvailableColumn,
    ...restrictedAvailableColumns,
    quantityAllocatedColumn,
    quantityOnHandColumn,
    quantityBackOrderedColumn,
    quantityOnOrderColumn,
  ];
};

export const vendorInventoryColumns = ({
  isAlternateVersionEnabled,
  alternateInventoryEnabled,
}: IVendorInventoryProps): ColDef[] => {
  const columns: ColDef[] = [
    {
      colId: 'location',
      headerName: t('headerLocation'),
      field: 'locationName',
      sort: SortDirection.ASC,
      filter: 'agTextColumnFilter',
      cellRenderer: LocationCell,
      cellRendererParams: { isAlternateVersionEnabled },
      valueGetter: ({ data }) => data && getLocationValue(data, isAlternateVersionEnabled),
    },
    {
      colId: 'uom',
      headerName: t('headerUom'),
      field: 'uom',
      maxWidth: 200,
      filter: 'agTextColumnFilter',
      valueFormatter: ({ value }) => value?.toUpperCase(),
    },
    {
      colId: 'quantityAvailable',
      headerName: t('headerAvailable'),
      field: 'quantityAvailable',
      maxWidth: 250,
      aggFunc: 'sum',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      valueGetter: ({ data }) => data && (Number(data.quantityAvailable) || 0),
      valueFormatter: ({ value }) => formatQuantity(value).toString(),
      cellStyle: { fontWeight: 'bold' },
    },
  ];

  if (isAlternateVersionEnabled) {
    columns.push({
      colId: 'quantityRestrictedAvailable',
      headerName: t('headerRestrictedAvailable'),
      field: 'quantityRestrictedAvailable',
      maxWidth: 250,
      aggFunc: 'sum',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      valueGetter: ({ data }) => data && (Number(data.quantityRestrictedAvailable) || 0),
      valueFormatter: ({ value }) => formatQuantity(value).toString(),
      cellStyle: { fontWeight: 'bold' },
    });
  }

  if (!alternateInventoryEnabled) {
    columns.push({
      colId: 'updatedAt',
      headerName: t('headerLastUpdated'),
      field: 'lastUpdateDate',
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
      valueFormatter: ({ value }) => value && formatDate(value),
    });
  }

  return columns;
};
